/**
 * AdminMarkenRechtstraegerSection — Marken-Stammdaten (Rechtsträger)
 * Issue #278 / #260
 *
 * Admin-UI zum Anlegen, Bearbeiten und Löschen von Marken inkl. rechtlicher
 * Angaben (HRB, USt-ID, IBAN, Anschrift) und Branding-Assets (Logo, Briefkopf,
 * Footer-Disclaimer, Absender-E-Mail). Fundament für E-Mail-Footer- und
 * PDF-Briefbogen-Integration (eigene Folge-Issues aus #260).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Building2, Plus, Edit2, Trash2, Save, X, Upload, FileText, Image as ImageIcon,
  Mail, CreditCard, ChevronDown, ChevronRight, Check,
} from 'lucide-react';
import { useMarken, type MarkeInsert } from '@/hooks/useMarken';
import type { Marke } from '@/lib/database.types';
import { toast } from 'sonner';

const EMPTY_FORM: MarkeInsert = {
  marken_name: '',
  rechtstraeger: '',
  business_unit: null,
  hrb_nr: null,
  ust_id: null,
  anschrift: null,
  iban: null,
  bic: null,
  logo_url: null,
  briefkopf_url: null,
  footer_disclaimer: null,
  absender_email: null,
  ist_aktiv: true,
  betrieb_id: null,
  typ: null,
};

function AssetPreview({
  path,
  label,
  getUrl,
}: {
  path: string | null;
  label: string;
  getUrl: (p: string | null | undefined) => Promise<string | null>;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getUrl(path).then((u) => {
      if (!cancelled) setUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [path, getUrl]);

  if (!path) {
    return <span className="text-[11px] text-gray-300 italic">Kein {label} hinterlegt</span>;
  }
  if (!url) {
    return <span className="text-[11px] text-gray-400">{label} lädt …</span>;
  }
  if (path.toLowerCase().endsWith('.pdf')) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline">
        <FileText className="w-3.5 h-3.5" /> {label} (PDF)
      </a>
    );
  }
  return <img src={url} alt={label} className="h-10 object-contain border border-gray-100 rounded bg-white p-1" />;
}

function MarkeCard({
  marke,
  onEdit,
  onDelete,
  getUrl,
}: {
  marke: Marke;
  onEdit: () => void;
  onDelete: () => void;
  getUrl: (p: string | null | undefined) => Promise<string | null>;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-5 h-5 text-gray-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-gray-800">{marke.marken_name}</p>
            {marke.ist_aktiv && (
              <span className="text-[9px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">aktiv</span>
            )}
          </div>
          <p className="text-[11px] text-gray-400">
            eine Marke der <span className="font-medium text-gray-500">{marke.rechtstraeger}</span>
          </p>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-0.5">HRB-Nr.</p>
              <p className="font-mono text-gray-700">{marke.hrb_nr || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-0.5">USt-ID</p>
              <p className="font-mono text-gray-700">{marke.ust_id || '—'}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-0.5">Anschrift</p>
              <p className="text-gray-700 whitespace-pre-wrap">{marke.anschrift || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-0.5 flex items-center gap-1"><CreditCard className="w-3 h-3" /> IBAN</p>
              <p className="font-mono text-gray-700">{marke.iban || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-0.5">BIC</p>
              <p className="font-mono text-gray-700">{marke.bic || '—'}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-0.5 flex items-center gap-1"><Mail className="w-3 h-3" /> Absender-E-Mail</p>
              <p className="text-gray-700">{marke.absender_email || '—'}</p>
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Footer-Disclaimer</p>
            <p className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 border border-gray-100">
              {marke.footer_disclaimer || '—'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Logo</p>
              <AssetPreview path={marke.logo_url} label="Logo" getUrl={getUrl} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Briefkopf</p>
              <AssetPreview path={marke.briefkopf_url} label="Briefkopf" getUrl={getUrl} />
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#9B1C1C] hover:bg-[#7F1D1D]"
            >
              <Edit2 className="w-3.5 h-3.5" /> Bearbeiten
            </button>
            <button
              onClick={onDelete}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-rose-600 border border-rose-200 hover:bg-rose-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Löschen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MarkeForm({
  initial,
  onCancel,
  onSubmit,
  onUploadAsset,
  submitLabel,
  mode,
}: {
  initial: MarkeInsert;
  onCancel: () => void;
  onSubmit: (values: MarkeInsert) => Promise<boolean>;
  onUploadAsset: (file: File, kind: 'logo' | 'briefkopf') => Promise<string | null>;
  submitLabel: string;
  mode: 'create' | 'edit';
}) {
  const [form, setForm] = useState<MarkeInsert>(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<'logo' | 'briefkopf' | null>(null);

  const update = <K extends keyof MarkeInsert>(key: K, value: MarkeInsert[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  const handleFile = async (file: File | undefined, kind: 'logo' | 'briefkopf') => {
    if (!file) return;
    setUploading(kind);
    const path = await onUploadAsset(file, kind);
    setUploading(null);
    if (path) {
      update(kind === 'logo' ? 'logo_url' : 'briefkopf_url', path);
      toast.success(`${kind === 'logo' ? 'Logo' : 'Briefkopf'} hochgeladen`);
    }
  };

  const handleSubmit = async () => {
    if (!form.marken_name.trim() || !form.rechtstraeger.trim()) {
      toast.error('Marken-Name und Rechtsträger sind Pflichtfelder');
      return;
    }
    setSaving(true);
    const ok = await onSubmit(form);
    setSaving(false);
    if (ok) onCancel();
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-medium text-gray-500">Marken-Name*</label>
          <input
            value={form.marken_name}
            onChange={(e) => update('marken_name', e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
            placeholder="z. B. MietGo"
            data-testid="marke-input-marken-name"
          />
        </div>
        <div>
          <label className="text-[10px] font-medium text-gray-500">Rechtsträger*</label>
          <input
            value={form.rechtstraeger}
            onChange={(e) => update('rechtstraeger', e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
            placeholder="z. B. LCS GmbH"
            data-testid="marke-input-rechtstraeger"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-medium text-gray-500">HRB-Nr.</label>
          <input
            value={form.hrb_nr ?? ''}
            onChange={(e) => update('hrb_nr', e.target.value || null)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] font-medium text-gray-500">USt-ID</label>
          <input
            value={form.ust_id ?? ''}
            onChange={(e) => update('ust_id', e.target.value || null)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
          />
        </div>
      </div>

      <div>
        <label className="text-[10px] font-medium text-gray-500">Anschrift</label>
        <textarea
          value={form.anschrift ?? ''}
          onChange={(e) => update('anschrift', e.target.value || null)}
          rows={2}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-medium text-gray-500">IBAN</label>
          <input
            value={form.iban ?? ''}
            onChange={(e) => update('iban', e.target.value || null)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] font-medium text-gray-500">BIC</label>
          <input
            value={form.bic ?? ''}
            onChange={(e) => update('bic', e.target.value || null)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
          />
        </div>
      </div>

      <div>
        <label className="text-[10px] font-medium text-gray-500">Absender-E-Mail</label>
        <input
          type="email"
          value={form.absender_email ?? ''}
          onChange={(e) => update('absender_email', e.target.value || null)}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
          placeholder="forderung@mietgo.de"
        />
      </div>

      <div>
        <label className="text-[10px] font-medium text-gray-500">Footer-Disclaimer</label>
        <textarea
          value={form.footer_disclaimer ?? ''}
          onChange={(e) => update('footer_disclaimer', e.target.value || null)}
          rows={3}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none"
          placeholder="MietGo – eine Marke der LCS GmbH · [Anschrift] · HRB [x] · USt-ID [x]"
        />
      </div>

      {mode === 'edit' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-medium text-gray-500 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Logo</label>
            <div className="mt-1 flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100">
                <Upload className="w-3.5 h-3.5" />
                {uploading === 'logo' ? 'Lädt …' : 'Datei wählen'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0], 'logo')}
                />
              </label>
              {form.logo_url && <span className="text-[11px] text-emerald-600 flex items-center gap-1"><Check className="w-3 h-3" /> {form.logo_url.split('/').pop()}</span>}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-medium text-gray-500 flex items-center gap-1"><FileText className="w-3 h-3" /> Briefkopf</label>
            <div className="mt-1 flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100">
                <Upload className="w-3.5 h-3.5" />
                {uploading === 'briefkopf' ? 'Lädt …' : 'Datei wählen'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,application/pdf"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0], 'briefkopf')}
                />
              </label>
              {form.briefkopf_url && <span className="text-[11px] text-emerald-600 flex items-center gap-1"><Check className="w-3 h-3" /> {form.briefkopf_url.split('/').pop()}</span>}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2.5 text-[11px] text-gray-500 flex items-start gap-2">
          <Upload className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
          <span>
            Logo und Briefkopf können nach dem Anlegen im Bearbeiten-Modus hochgeladen werden.
          </span>
        </div>
      )}

      <label className="flex items-center gap-2 text-xs text-gray-600">
        <input
          type="checkbox"
          checked={form.ist_aktiv}
          onChange={(e) => update('ist_aktiv', e.target.checked)}
          className="w-4 h-4"
        />
        Marke ist aktiv (wird von Email- und PDF-Generator verwendet)
      </label>

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={onCancel}
          className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Abbrechen
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#9B1C1C] hover:bg-[#7F1D1D] disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="marke-submit"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

export default function AdminMarkenRechtstraegerSection() {
  const { data, loading, create, update, remove, uploadAsset, getAssetUrl } = useMarken();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const editingMarke = useMemo(
    () => (editingId ? data.find((m) => m.id === editingId) : null),
    [data, editingId],
  );

  const handleUploadForEditing = async (file: File, kind: 'logo' | 'briefkopf') => {
    if (!editingId) {
      toast.error('Marke zuerst speichern');
      return null;
    }
    return uploadAsset(editingId, file, kind);
  };

  // Im Create-Modus werden die Upload-Blöcke nicht gerendert (siehe MarkeForm),
  // daher liefert der Handler hier nur einen No-Op zurück.
  const handleUploadForCreate = async () => null;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-bold text-gray-900">Marken (Rechtsträger)</h2>
            <p className="text-xs text-gray-400">
              Stammdaten für ausgehende Korrespondenz: Rechtsträger, HRB, USt-ID, Bankverbindung, Logo, Briefkopf.
            </p>
          </div>
          {!creating && editingId === null && (
            <button
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#9B1C1C] hover:bg-[#7F1D1D]"
              data-testid="marke-neu"
            >
              <Plus className="w-3.5 h-3.5" /> Neue Marke
            </button>
          )}
        </div>
      </div>

      {creating && (
        <MarkeForm
          initial={EMPTY_FORM}
          submitLabel="Anlegen"
          mode="create"
          onCancel={() => setCreating(false)}
          onSubmit={(values) => create(values)}
          onUploadAsset={handleUploadForCreate}
        />
      )}

      {editingMarke && (
        <MarkeForm
          initial={{
            marken_name: editingMarke.marken_name,
            rechtstraeger: editingMarke.rechtstraeger,
            hrb_nr: editingMarke.hrb_nr,
            ust_id: editingMarke.ust_id,
            anschrift: editingMarke.anschrift,
            iban: editingMarke.iban,
            bic: editingMarke.bic,
            logo_url: editingMarke.logo_url,
            briefkopf_url: editingMarke.briefkopf_url,
            footer_disclaimer: editingMarke.footer_disclaimer,
            absender_email: editingMarke.absender_email,
            business_unit: editingMarke.business_unit,
            ist_aktiv: editingMarke.ist_aktiv,
            betrieb_id: editingMarke.betrieb_id,
            typ: editingMarke.typ,
          }}
          submitLabel="Speichern"
          mode="edit"
          onCancel={() => setEditingId(null)}
          onSubmit={(values) => update(editingMarke.id, values)}
          onUploadAsset={handleUploadForEditing}
        />
      )}

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-[#9B1C1C] rounded-full animate-spin" />
        </div>
      )}

      {!loading && data.length === 0 && !creating && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center shadow-sm">
          <Building2 className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-400">Keine Marken konfiguriert</p>
          <button
            onClick={() => setCreating(true)}
            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#9B1C1C] hover:bg-[#7F1D1D]"
          >
            <Plus className="w-3.5 h-3.5" /> Erste Marke anlegen
          </button>
        </div>
      )}

      {!loading &&
        data.map((marke) => (
          <MarkeCard
            key={marke.id}
            marke={marke}
            onEdit={() => {
              setEditingId(marke.id);
              setCreating(false);
            }}
            onDelete={async () => {
              if (!confirm(`Marke „${marke.marken_name}" wirklich löschen?`)) return;
              await remove(marke.id);
            }}
            getUrl={getAssetUrl}
          />
        ))}
    </div>
  );
}
